import { type AssetsManifest, type Container } from 'pixi.js';
import { MagicLayout } from '@magic/layout';
import { settingsStore } from '../stores/settings.store';
import { getGameConfig } from 'config/game.config';
import { RoundController } from 'controllers/stateControllers/Round.controller';
import { ReelController } from 'controllers/stateControllers/Reel.controller';
import { BalanceController } from 'controllers/Balance.controller';
import { isIdle, setGameState, apiStore, getSpinMode } from '../stores/api.store';
import type { APIController, RoundData } from '../utils/api.types';
import { findSpineBundles } from '../utils/spineBundles';
import { clearFreeSpinsProgress } from '../utils/freeSpinsStorage';

export class MainController {
    spine: MagicLayout;
    private roundController: RoundController;
    private reelController: ReelController;
    private balanceController: BalanceController;
    private spinInFlight = false;
    private endRoundInFlight: Promise<void> | null = null;

    constructor(
        private manifest: AssetsManifest,
        private globalView: Container,
        private api: APIController,
    ) {
        this.spine = new MagicLayout({
            debug: settingsStore.getState().spineDebug,
            skipAttachingSpinesPatterns: ['symbol'],
        });

        const { names, folder } = findSpineBundles(this.manifest);
        const bundles = this.manifest.bundles.filter((bundle) => names.includes(bundle.name));

        this.spine.createInstancesFromManifest({ bundles }, folder);

        this.globalView.addChild(this.spine);

        this.reelController = new ReelController(this.spine);
        this.roundController = new RoundController(this.spine, this.reelController, (index) =>
            this.recordRoundEvent(index),
        );
        this.balanceController = new BalanceController(this.api);

        this.addSubscriptions();
        this.balanceController.start();
    }

    private addSubscriptions() {
        apiStore.subscribe(
            (state) => state.state,
            (state, previousState) => {
                if (previousState === 'loading' && state === 'idle') {
                    this.startGame();
                }

                if (state === 'spin') {
                    this.roundController.setTotalWin('-');
                }
            },
        );

        apiStore.subscribe(
            (state) => state.balance,
            (balance) => balance && this.roundController.setBalance(balance),
        );

        apiStore.subscribe(
            (state) => state.betAmount,
            (betAmount) => this.roundController.setBet(betAmount),
        );

        this.roundController.setBet(apiStore.getState().betAmount);

        apiStore.subscribe(
            (state) => state.round,
            async (round) => {
                if (!round) return;

                const roundData = round as unknown as RoundData;

                await this.roundController.playState(roundData);

                // The presentation is over — the player has seen the result, so
                // settle the round now. The payout is only credited by EndRound,
                // so this is what puts the win in the wallet and, through the
                // response balance, on screen while the win is still displayed.
                // Deferring it to the next spin click (as a winning round used
                // to be) leaves the balance showing the pre-payout amount for as
                // long as the player looks at it, and the credit then lands
                // milliseconds before the next bet deduction overwrites it.
                //
                // The round stays open for the whole sequence, so a refresh
                // mid-win still replays it; only a refresh after the sequence
                // has finished now starts from a clean board, which is correct —
                // that win is paid.
                //
                // Only settle while this round is still the current one: a spin
                // clicked during the sequence tail (finalWin holds ~1s after
                // going idle) settles this round itself and may already have a
                // NEW round active — settling then would end that fresh round
                // mid-book (e.g. right as free spins start).
                if (apiStore.getState().round?.betID === round.betID) {
                    await this.settleRound();
                }
            },
        );

        this.spine.animations.addEventListener('spin_click', async () => this.onSpinClick());

        this.spine.animations.addEventListener('bet_minus_click', async () => {
            apiStore.getState().actions.betDown();
        });

        this.spine.animations.addEventListener('bet_plus_click', async () => {
            apiStore.getState().actions.betUp();
        });
    }

    private onSpinStart() {
        this.roundController.onSpinStart();
        // solo, so any lingering symbol state (active/inactive) is cleared
        this.spine.animations.playByName('blur', true);
        // loops the spin animation (filling random symbols on update/symbol-X)
        // until the round result triggers roundController -> reelController.reveal()
        this.reelController.spin();
    }

    private async onSpinClick() {
        // spinInFlight covers the async gap before setGameState('spin') — the
        // game state alone lets a double-click send play twice
        if (this.spinInFlight || !isIdle() || this.roundController.freeSpinsActive) {
            return;
        }
        this.spinInFlight = true;

        this.spine.texts.set('win', ''); // reset win text on each spin start
        this.spine.texts.set('total_win', ''); // reset total win text on each spin start

        try {
            const amount = apiStore.getState().betAmount;

            // close the previous round before playing the next one. It was
            // already settled when its sequence finished, so this normally
            // resolves at once (or waits on that request if it is still in
            // flight) — it only has real work to do when the player clicked
            // through the tail of the sequence, before the settle got there
            await this.settleRound();

            if (amount) {
                // TODO: inverstigate if we need to use this state
                setGameState('spin');
                this.onSpinStart();

                // free spins play the same book against the FREESPINS mode; the
                // base game bet mode is only for player-paid spins
                const spinResponce = await this.api.spin(amount, getSpinMode());

                if (spinResponce) {
                    apiStore.getState().actions.setPlayResponse(spinResponce);
                    // the play response is the newest balance there is — drop any
                    // poll still in flight, it answered with the pre-bet value
                    this.balanceController.apply(spinResponce.balance);
                    setGameState('reveal');
                } else {
                    await this.abortSpin();
                }
            }
        } catch (error) {
            console.error('Failed to start spin:', error);
            await this.abortSpin();
        } finally {
            this.spinInFlight = false;
        }
    }

    /**
     * Ends the still-open backend round, crediting its payout. Every round is
     * settled as soon as its sequence finishes, so the next spin click doesn't
     * have to pay for an EndRound request first. The in-flight request is
     * shared: a spin clicked during the tail of the sequence awaits the same
     * promise instead of sending EndRound a second time.
     */
    private settleRound(): Promise<void> {
        if (apiStore.getState().roundActive) {
            this.endRoundInFlight ??= (async () => {
                try {
                    // the payout is credited on EndRound, so its response holds
                    // the balance the player ends the round with — the play
                    // response only ever had the bet deducted
                    const response = await this.api.endRound();

                    this.balanceController.apply(response.balance);
                } catch (error) {
                    // a failed EndRound must not block the game: if the round is
                    // genuinely still open, the play request's round-already-active
                    // fallback ends it again
                    console.error('Failed to end round:', error);
                }

                apiStore.getState().actions.markRoundEnded();

                // the round is closed, so there is nothing left to replay or
                // resume — progress only ever covers a round still open on the
                // backend (a free spins round now stays open until this point,
                // one spin click after its last reveal)
                clearFreeSpinsProgress();

                this.endRoundInFlight = null;
            })();
        }

        return this.endRoundInFlight ?? Promise.resolve();
    }

    /**
     * Confirms a played book event index to the backend (`/bet/event`).
     * Fire-and-forget: progress tracking must never block or fail the
     * animation sequence.
     */
    private recordRoundEvent(index: number) {
        Promise.resolve(this.api.event(String(index))).catch((error) =>
            console.error('Failed to record round event:', error),
        );
    }

    /** Settles the reels without a result and returns to idle after a failed spin request. */
    private async abortSpin() {
        await this.reelController.stop();
        setGameState('idle');
    }

    private async startGame() {
        const { balance, round } = apiStore.getState();

        if (balance?.amount && !round) {
            this.reelController.fillBoardWithRandomSymbols();
        }
    }

    // TODO: move this to spineLayout or create separate SpineResizer utility
    resize(w = window.innerWidth, h = window.innerHeight) {
        this.spine.x = w / 2;
        this.spine.y = h / 2 + 20;

        const { minHeight, minWidth } = getGameConfig();

        let scaleX = 1;
        let scaleY = 1;

        if (w < minWidth) {
            scaleX = w / minWidth;
        }

        if (h < minHeight) {
            scaleY = h / minHeight;
        }

        this.spine.scale.set(Math.min(scaleX, scaleY));
    }
}
